import { DateTime } from "luxon";
import { getScheduleVisitorDisplayName } from "../../../components/Schedule/functions/schedule-visitor-display-name.function";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type { CheckInSettings } from "../../Login/typings/login.types";
import type {
  Dashboard2DayBookingSummary,
  DayWorkStatus
} from "../types/dashboard2-work-status.types";
import {
  classifyDashboard2BookingBucket,
  isOwnWorkplacePrimaryForCheckIn,
  selectPrimaryBookingForDay,
  sortBookingsByStart
} from "./dashboard2-primary-booking.utils";
import {
  resolveScheduleDisplayParticipantTags,
  resolveScheduleDisplayTypeLabel
} from "./dashboard2-booking-type-label.utils";
import {
  formatScheduleTimeRange,
  resolveScheduleBookingDetails,
  schedulesForUserOnDay
} from "./dashboard2-schedule-filter.utils";

export type DeriveDayWorkStatusOptions = {
  mobileWorkingLabel: string;
  holidayName?: string;
  notSetLabel?: string;
  resolveBookingTypeLabel?: (bookingType: string) => string;
  resolveExternalLabel?: () => string;
  checkinSettings?: ReadonlyArray<CheckInSettings>;
  /** Locale-aware Luxon time token (e.g. `HH:mm` for German, `hh:mm a` for English). */
  timeFormat?: string;
};

export function resolveScheduleBookingTypeLabel(
  bookingType: string,
  resolveBookingTypeLabel?: (bookingType: string) => string
): string {
  if (resolveBookingTypeLabel) {
    return resolveBookingTypeLabel(bookingType);
  }

  return bookingType;
}

/** Visit-only visitor rows omit desk/inventory; bookPlace guests keep it on the secondary line. */
function shouldShowVisitorBookingInventory(schedule: BookingScheduleInterface): boolean {
  if (schedule.bookPlace === true) {
    return true;
  }

  if (schedule.bookPlace === false) {
    return false;
  }

  const guestBookPlace = schedule.visitorBookings?.[0]?.bookPlace;

  if (guestBookPlace === true) {
    return true;
  }

  if (guestBookPlace === false) {
    return false;
  }

  return schedule.isVisitOnly !== true;
}

function buildDayBookingSummaryLines(
  schedule: BookingScheduleInterface,
  details: ReturnType<typeof resolveScheduleBookingDetails>,
  timeRange: string
): { primaryLine: string; secondaryLine: string | undefined } {
  const startTime = timeRange.split("–")[0]?.trim();
  const locationPart = details.locationName ?? details.floorName;
  const inventoryPart =
    details.inventoryName && details.inventoryName !== locationPart
      ? details.inventoryName
      : undefined;
  const defaultPrimaryLine = [inventoryPart ?? locationPart, startTime].filter(Boolean).join(", ");
  const defaultSecondaryLine = locationPart
    ? [locationPart, details.floorName, timeRange].filter(Boolean).join(" · ")
    : timeRange;

  const bucket = classifyDashboard2BookingBucket(schedule);
  const visitorName = getScheduleVisitorDisplayName(schedule);

  if (bucket === "visitor" && visitorName) {
    const showInventory = shouldShowVisitorBookingInventory(schedule);

    if (showInventory) {
      const locationLabel = details.locationName ?? details.inventoryName;
      let detailPart: string | undefined;

      if (details.inventoryName && details.inventoryName !== visitorName) {
        detailPart = details.inventoryName;
      } else if (locationLabel && locationLabel !== visitorName) {
        detailPart = locationLabel;
      }

      return {
        primaryLine: [visitorName, startTime].filter(Boolean).join(", "),
        secondaryLine: detailPart
          ? [detailPart, details.floorName, timeRange].filter(Boolean).join(" · ")
          : defaultSecondaryLine || undefined
      };
    }

    const visitOnlySecondaryLine = [details.locationName, timeRange].filter(Boolean).join(" · ");

    return {
      primaryLine: [visitorName, startTime].filter(Boolean).join(", "),
      secondaryLine: visitOnlySecondaryLine || undefined
    };
  }

  return {
    primaryLine: defaultPrimaryLine,
    secondaryLine: defaultSecondaryLine || undefined
  };
}

export function mapScheduleToDayBookingSummary(
  schedule: BookingScheduleInterface,
  options: Pick<
    DeriveDayWorkStatusOptions,
    "mobileWorkingLabel" | "resolveBookingTypeLabel" | "resolveExternalLabel" | "timeFormat"
  >
): Dashboard2DayBookingSummary {
  const typeLabel = resolveScheduleDisplayTypeLabel(schedule, {
    resolveBookingTypeLabel: options.resolveBookingTypeLabel,
    resolveExternalLabel: options.resolveExternalLabel
  });
  const timeRange = formatScheduleTimeRange(schedule, options.timeFormat);
  const details = resolveScheduleBookingDetails(schedule, options.mobileWorkingLabel);
  const { primaryLine, secondaryLine } = buildDayBookingSummaryLines(schedule, details, timeRange);

  const participantTags = resolveScheduleDisplayParticipantTags(schedule, typeLabel);

  return {
    id: String(schedule.id),
    bookingType: schedule.bookingType,
    typeLabel,
    primaryLine: primaryLine || typeLabel,
    secondaryLine: secondaryLine || undefined,
    locationName: schedule.locationName,
    isVisitOnly: schedule.isVisitOnly,
    external: schedule.external,
    externalEmail: schedule.externalEmail,
    externalName: schedule.externalName,
    externalCompany: schedule.externalCompany,
    visitorBookings: schedule.visitorBookings,
    participantTags: participantTags.length > 0 ? participantTags : undefined
  };
}

/** Minimal schedule slice for ScheduleEventContentIcon on additional booking pills. */
export function bookingSummaryToIconSchedule(
  booking: Dashboard2DayBookingSummary
): BookingScheduleInterface {
  return {
    bookingType: booking.bookingType,
    locationName: booking.locationName,
    isVisitOnly: booking.isVisitOnly,
    external: booking.external,
    externalEmail: booking.externalEmail,
    externalName: booking.externalName,
    externalCompany: booking.externalCompany,
    visitorBookings: booking.visitorBookings,
    userBookedFor: ""
  } as BookingScheduleInterface;
}

export function resolveWorkplaceCheckInFlags(
  schedule: BookingScheduleInterface | undefined,
  checkinSettings?: ReadonlyArray<CheckInSettings>
): { checkInAvailable?: boolean; checkedIn?: boolean } {
  if (!schedule) {
    return {};
  }

  const checkinBufferTimePerBookingType =
    checkinSettings?.find(setting => setting.bookingType === schedule.bookingType)
      ?.checkinBufferTime ?? schedule.checkInPeriod;

  const checkInAvailable =
    !schedule.checkedIn && checkinBufferTimePerBookingType > 0 && schedule.checkInPeriod !== -1;

  return {
    checkInAvailable,
    checkedIn: schedule.checkedIn
  };
}

function buildAdditionalBookingsFromDay(
  dayBookings: BookingScheduleInterface[],
  primary: BookingScheduleInterface | undefined,
  options: DeriveDayWorkStatusOptions
): Dashboard2DayBookingSummary[] {
  const extras = sortBookingsByStart(dayBookings.filter(booking => booking.id !== primary?.id));

  return extras.map(schedule => mapScheduleToDayBookingSummary(schedule, options));
}

function buildOfficeStatusFieldsFromPrimary(
  primary: BookingScheduleInterface,
  options: DeriveDayWorkStatusOptions
): Pick<DayWorkStatus, "label" | "detail" | "floorName" | "timeRange"> {
  const details = resolveScheduleBookingDetails(primary, options.mobileWorkingLabel);
  const timeRange = formatScheduleTimeRange(primary, options.timeFormat);
  const bucket = classifyDashboard2BookingBucket(primary);
  const visitorName = getScheduleVisitorDisplayName(primary);

  if (bucket === "visitor" && visitorName) {
    const showInventory = shouldShowVisitorBookingInventory(primary);
    const locationLabel = details.locationName ?? details.inventoryName;
    let deskDetail: string | undefined;

    if (showInventory && details.inventoryName && details.inventoryName !== visitorName) {
      deskDetail = details.inventoryName;
    } else if (details.locationName && details.locationName !== visitorName) {
      deskDetail = details.locationName;
    } else if (showInventory && locationLabel && locationLabel !== visitorName) {
      deskDetail = locationLabel;
    }

    return {
      label: visitorName,
      detail: deskDetail,
      floorName: showInventory ? details.floorName : undefined,
      timeRange
    };
  }

  if (bucket === "conference" || bucket === "other") {
    const primaryLabel = details.inventoryName || details.locationName;
    const locationLabel = details.locationName;

    return {
      label: primaryLabel ?? "",
      detail:
        details.inventoryName && locationLabel && locationLabel !== primaryLabel
          ? locationLabel
          : details.floorName,
      floorName: details.floorName,
      timeRange
    };
  }

  const locationLabel = details.locationName ?? details.inventoryName;
  const deskDetail =
    details.inventoryName && details.inventoryName !== locationLabel
      ? details.inventoryName
      : undefined;

  return {
    label: locationLabel,
    detail: deskDetail,
    floorName: details.floorName,
    timeRange
  };
}

function attachDerivedStatusExtras(
  status: DayWorkStatus,
  primary: BookingScheduleInterface | undefined,
  dayBookings: BookingScheduleInterface[],
  options: DeriveDayWorkStatusOptions
): DayWorkStatus {
  const additionalBookings = buildAdditionalBookingsFromDay(dayBookings, primary, options);
  const checkInEligible =
    status.kind === "office" && !status.isFinished && isOwnWorkplacePrimaryForCheckIn(primary);
  const checkInFlags = checkInEligible
    ? resolveWorkplaceCheckInFlags(primary, options.checkinSettings)
    : {};

  return {
    ...status,
    ...checkInFlags,
    additionalBookings: additionalBookings.length > 0 ? additionalBookings : undefined,
    secondaryBookingCount:
      additionalBookings.length > 0 ? additionalBookings.length : status.secondaryBookingCount
  };
}

export function countPlannedWorkdays(statuses: readonly DayWorkStatus[]): {
  planned: number;
  total: number;
} {
  const workdayStatuses = statuses.filter(status => status.kind !== "public_holiday");
  const total = workdayStatuses.length;
  const planned = workdayStatuses.filter(
    status => status.kind === "office" || status.kind === "mobile_working"
  ).length;

  return { planned, total };
}

export function deriveDayWorkStatusForUser(
  schedules: BookingScheduleInterface[],
  userId: string,
  day: DateTime,
  options: DeriveDayWorkStatusOptions
): DayWorkStatus {
  if (options.holidayName) {
    return {
      kind: "public_holiday",
      label: options.holidayName,
      holidayName: options.holidayName
    };
  }

  const dayBookings = schedulesForUserOnDay(schedules, userId, day);
  const notSetLabel = options.notSetLabel ?? "Not set";
  const isToday = day.toFormat("yyyy-MM-dd") === DateTime.now().toFormat("yyyy-MM-dd");
  const { primary, isFinished } = selectPrimaryBookingForDay(dayBookings, { isToday });

  if (!primary) {
    return { kind: "unplanned", label: notSetLabel };
  }

  const bucket = classifyDashboard2BookingBucket(primary);

  if (bucket === "mobile_working") {
    return attachDerivedStatusExtras(
      {
        kind: "mobile_working",
        label: options.mobileWorkingLabel,
        isFinished,
        timeRange: formatScheduleTimeRange(primary, options.timeFormat)
      },
      primary,
      dayBookings,
      options
    );
  }

  return attachDerivedStatusExtras(
    {
      kind: "office",
      isFinished,
      ...buildOfficeStatusFieldsFromPrimary(primary, options)
    },
    primary,
    dayBookings,
    options
  );
}
